import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Play, Power, RefreshCw, ChevronDown, ChevronRight, ClipboardList } from "lucide-react";

/**
 * Growth Engine · Email Workflows sub-tab.
 *
 * Lists the 4 pluggable workflows (Lead Gen, Nurture, Follow-up,
 * Content Gen) — toggle enabled, edit cron, "Run now" trigger, and
 * view the last 20 execution rows per workflow.
 */
export default function GrowthEngineWorkflows() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [execs, setExecs] = useState({}); // { workflow_key: [execution rows] }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/growth/workflows");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Load workflows failed: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadExecs = async (key) => {
    try {
      const { data } = await api.get(`/admin/growth/workflows/${key}/executions?limit=20`);
      setExecs((prev) => ({ ...prev, [key]: data.items || [] }));
    } catch (e) {
      toast.error("Load executions failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const toggle = async (key, next) => {
    try {
      await api.patch(`/admin/growth/workflows/${key}`, { enabled: next });
      toast.success(`${key} ${next ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const saveCron = async (key, cron) => {
    try {
      await api.patch(`/admin/growth/workflows/${key}`, { schedule: cron });
      toast.success("Schedule saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const runNow = async (key) => {
    try {
      await api.post(`/admin/growth/workflows/${key}/run`);
      toast.success("Workflow triggered — check executions in a few seconds");
      setTimeout(() => loadExecs(key), 3000);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const toggleExpand = (key) => {
    if (expanded === key) { setExpanded(null); return; }
    setExpanded(key); loadExecs(key);
  };

  return (
    <div className="space-y-4" data-testid="ge-workflows">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          {items.length} workflows · {items.filter((w) => w.enabled).length} enabled
        </div>
        <button onClick={load} className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="space-y-3">
        {loading && items.length === 0 && (
          <div className="text-sm text-slate-400 p-6 text-center">Loading workflows…</div>
        )}
        {items.map((w) => (
          <div key={w.key} className={"bg-white border rounded-lg overflow-hidden " +
                                       (w.enabled ? "border-emerald-300" : "border-slate-200")}
               data-testid={`wf-row-${w.key}`}>
            <header className="p-4 flex items-start gap-4">
              <div className={"mt-0.5 w-2 h-2 rounded-full " + (w.enabled ? "bg-emerald-500 animate-pulse" : "bg-slate-300")}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-bold text-slate-900">{w.name}</h4>
                  <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{w.key}</span>
                  {w.trigger_events?.length > 0 && (
                    <span className="text-[10px] font-mono bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" title="Event triggers">
                      event: {w.trigger_events.join(", ")}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{w.description}</p>
                <div className="flex items-center gap-3 mt-2 text-xs">
                  <label className="flex items-center gap-1.5">
                    <span className="text-slate-500 font-semibold">Cron:</span>
                    <input
                      type="text" defaultValue={w.schedule || ""}
                      onBlur={(e) => e.target.value !== (w.schedule || "") && saveCron(w.key, e.target.value)}
                      placeholder="e.g. 0 9 * * *"
                      className="text-[11px] font-mono px-2 py-1 border border-slate-200 rounded w-32"
                      data-testid={`wf-cron-${w.key}`}
                    />
                  </label>
                  {w.last_run_at && (
                    <span className="text-slate-400">
                      Last run · {new Date(w.last_run_at).toLocaleString()}
                      {w.last_counts && Object.keys(w.last_counts).length > 0 && (
                        <span className="ml-2">
                          ✓ {w.last_counts.succeeded || 0} / ✗ {w.last_counts.failed || 0}
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => runNow(w.key)}
                  className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:border-amber-500 hover:text-amber-700 flex items-center gap-1"
                  data-testid={`wf-run-${w.key}`}
                >
                  <Play size={12} /> Run now
                </button>
                <button
                  onClick={() => toggle(w.key, !w.enabled)}
                  className={"text-xs px-3 py-1.5 rounded font-semibold flex items-center gap-1 " +
                              (w.enabled
                                ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200")}
                  data-testid={`wf-toggle-${w.key}`}
                >
                  <Power size={12} /> {w.enabled ? "Enabled" : "Disabled"}
                </button>
                <button
                  onClick={() => toggleExpand(w.key)}
                  className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
                  data-testid={`wf-expand-${w.key}`}
                >
                  {expanded === w.key ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                  <ClipboardList size={12}/>
                </button>
              </div>
            </header>

            {expanded === w.key && (
              <div className="border-t border-slate-100 bg-slate-50">
                <div className="p-4 space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Last 20 executions</div>
                  {(execs[w.key] || []).length === 0 && (
                    <div className="text-xs text-slate-400 py-3">
                      No executions yet — click <span className="font-semibold">Run now</span> to trigger this workflow.
                    </div>
                  )}
                  {(execs[w.key] || []).map((e) => (
                    <div key={e.id} className="text-xs bg-white border border-slate-100 rounded p-2.5 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={"px-1.5 py-0.5 rounded text-[10px] font-bold uppercase " +
                                        (e.status === "ok" ? "bg-emerald-100 text-emerald-800" :
                                         e.status === "running" ? "bg-amber-100 text-amber-800" :
                                         "bg-red-100 text-red-800")}>
                          {e.status}
                        </span>
                        <span className="text-slate-500 text-[10px] font-mono">{e.trigger}</span>
                        <span className="text-slate-400 text-[10px]">
                          {e.started_at ? new Date(e.started_at).toLocaleString() : ""}
                        </span>
                        <span className="ml-auto text-slate-600 tabular-nums">
                          ✓ {e.counts?.succeeded || 0} / ✗ {e.counts?.failed || 0} / total {e.counts?.attempted || 0}
                        </span>
                      </div>
                      {e.error && <div className="text-red-700 text-[11px] font-mono bg-red-50 p-1.5 rounded">{e.error}</div>}
                      {e.log_lines?.length > 0 && (
                        <details className="text-[10px] font-mono">
                          <summary className="cursor-pointer text-slate-500 hover:text-slate-800">
                            {e.log_lines.length} log lines
                          </summary>
                          <div className="mt-1 p-2 bg-slate-900 text-slate-200 rounded max-h-40 overflow-y-auto">
                            {e.log_lines.map((ln, i) => <div key={i}>{ln}</div>)}
                          </div>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="text-[11px] text-slate-400 pt-3 border-t">
        <span className="font-semibold text-slate-500">Note:</span> Runs as Python jobs inside the CRM
        (APScheduler + Mailjet + Claude via Emergent LLM). The workflow config is n8n-compatible —
        we can swap to a real n8n instance later without changing this UI.
      </div>
    </div>
  );
}
